// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: decides when someone starts and stops talking from microphone levels.
// The noise floor adapts to the room while it is quiet, so a fan or a busy
// office does not count as speech; a turn ends after a short pause.

export type VoiceActivityEvent = 'speech-start' | 'speech-end' | null;

export interface VoiceActivityOptions {
	/** How far above the noise floor counts as speech. */
	marginDb: number;
	/** Speech must last this long before a turn starts (ignores clicks and coughs). */
	startMs: number;
	/** Quiet this long ends the turn. */
	endMs: number;
}

// 700 ms of quiet ends a turn: long enough for a breath mid-sentence, short enough that answers feel quick.
export const LISTENING: VoiceActivityOptions = {marginDb: 12, startMs: 160, endMs: 700};
// While the Assistant is speaking, echo can leak into the mic; ask for a clearer, longer voice to interrupt.
export const BARGE_IN: VoiceActivityOptions = {marginDb: 20, startMs: 320, endMs: 900};

const MIN_FLOOR_DB = -75;
const MIN_SPEECH_DB = -52;

/** RMS level of a block of time-domain samples, in dBFS. */
export function levelDb(samples: Float32Array): number {
	let sum = 0;
	for (const sample of samples) sum += sample * sample;
	const rms = Math.sqrt(sum / Math.max(1, samples.length));
	return rms > 0 ? Math.max(-100, 20 * Math.log10(rms)) : -100;
}

export class VoiceActivityDetector {
	floorDb = -60;
	speaking = false;
	private above = 0;
	private below = 0;

	constructor(private options: VoiceActivityOptions = LISTENING) {}

	setOptions(options: VoiceActivityOptions): void {
		this.options = options;
		this.reset();
	}

	reset(): void {
		this.speaking = false;
		this.above = 0;
		this.below = 0;
	}

	/** 0..1 loudness above the floor, for the orb animation. */
	intensity(db: number): number {
		return Math.min(1, Math.max(0, (db - this.floorDb - 4) / 30));
	}

	push(db: number, elapsedMs: number): VoiceActivityEvent {
		const loud = db > this.floorDb + this.options.marginDb && db > MIN_SPEECH_DB;
		if (!loud && !this.speaking) {
			// Track the room: fall quickly to quieter levels, rise over a couple of seconds to louder ones.
			const rate = 1 - Math.exp(-elapsedMs / (db < this.floorDb ? 150 : 1500));
			this.floorDb = Math.max(MIN_FLOOR_DB, this.floorDb + (db - this.floorDb) * rate);
		}
		if (loud) {
			this.above += elapsedMs;
			this.below = 0;
			if (!this.speaking && this.above >= this.options.startMs) {
				this.speaking = true;
				return 'speech-start';
			}
		} else {
			this.below += elapsedMs;
			if (this.below >= 80) this.above = 0;
			if (this.speaking && this.below >= this.options.endMs) {
				this.speaking = false;
				this.above = 0;
				return 'speech-end';
			}
		}
		return null;
	}
}
